import { Link, NavLink } from "react-router-dom";
import { MagnifyingGlass, Sparkle, Camera, UserPlus, UserCircle, Scales } from "@phosphor-icons/react";
import { useUser } from "../lib/UserContext";

export default function Header({ onOpenSearch, onOpenScan, onOpenRegister }) {
  const { user, logout } = useUser();
  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 bg-[#F7F5F0]/95 backdrop-blur border-b border-[#E5E2DC]"
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex items-center justify-between gap-4">
        <Link to="/" data-testid="site-logo" className="flex items-center gap-2.5 group shrink-0">
          <div className="w-8 h-8 bg-[#1C1B1A] text-[#F7F5F0] flex items-center justify-center">
            <Sparkle size={15} weight="fill" />
          </div>
          <div className="leading-none">
            <div className="font-heading font-medium text-[16px] tracking-tight">
              Cosmetovigilance<span className="text-[#A66E4E]"> · </span>
              <span className="font-accent italic">GCRS</span>
            </div>
            <div className="eyebrow text-[9px] mt-1">Global Cosmetic Surveillance</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {[
            { to: "/", label: "Dashboard" },
            { to: "/search", label: "Search" },
            { to: "/compare", label: "Compare" },
            { to: "/complaints", label: "Complaints" },
            { to: "/alerts", label: "Alerts" },
            { to: "/messages", label: "Messages" },
          ].map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-3 lg:px-4 py-2 text-sm font-body transition-colors ${
                  isActive
                    ? "text-[#1C1B1A] border-b-2 border-[#A66E4E]"
                    : "text-[#6E6B65] hover:text-[#1C1B1A] border-b-2 border-transparent"
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            type="button"
            data-testid="header-search-btn"
            onClick={onOpenSearch}
            className="hidden sm:flex items-center gap-2 px-3 py-2 border border-[#E5E2DC] hover:border-[#A66E4E] text-sm text-[#6E6B65] transition-colors bg-white"
          >
            <MagnifyingGlass size={15} />
            <span className="hidden lg:inline">Search…</span>
            <span className="hidden xl:inline ml-3 eyebrow text-[9px] bg-[#EFECE6] px-1.5 py-0.5">/</span>
          </button>
          <button
            type="button"
            data-testid="header-scan-btn"
            onClick={onOpenScan}
            title="Scan a product"
            className="w-10 h-10 flex items-center justify-center border border-[#E5E2DC] text-[#1C1B1A] hover:bg-[#EFECE6] transition-colors"
          >
            <Camera size={16} />
          </button>
          {user ? (
            <button
              type="button"
              data-testid="header-user-chip"
              onClick={logout}
              title={`Signed in as ${user.name} — click to sign out`}
              className="flex items-center gap-2 px-3 py-2 border border-[#E5E2DC] hover:border-[#A66E4E] text-[#1C1B1A] hover:bg-[#EFECE6] transition-colors text-sm font-heading"
            >
              <UserCircle size={16} weight="regular" />
              <span className="hidden sm:inline max-w-[10ch] truncate">{user.name.split(" ")[0]}</span>
            </button>
          ) : (
            <button
              type="button"
              data-testid="header-register-btn"
              onClick={onOpenRegister}
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-[#1C1B1A] text-white text-[11px] uppercase tracking-[0.18em] font-semibold hover:bg-[#A66E4E] transition-colors"
            >
              <UserPlus size={14} weight="bold" />
              Free sign-up
            </button>
          )}
        </div>
      </div>
      {/* Mobile nav row */}
      <div className="md:hidden flex overflow-x-auto border-t border-[#E5E2DC] bg-white">
        {[
          { to: "/", label: "Dashboard" },
          { to: "/search", label: "Search" },
          { to: "/compare", label: "Compare" },
          { to: "/complaints", label: "Complaints" },
          { to: "/alerts", label: "Alerts" },
          { to: "/messages", label: "Messages" },
        ].map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/"}
            className={({ isActive }) =>
              `px-4 py-2 text-[12px] whitespace-nowrap ${
                isActive
                  ? "text-[#1C1B1A] border-b-2 border-[#A66E4E] font-semibold"
                  : "text-[#6E6B65] border-b-2 border-transparent"
              }`
            }
          >
            {item.label}
          </NavLink>
        ))}
      </div>
    </header>
  );
}
